 import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Chip,
  Pagination,
  Tabs,
  Tab,
  Divider,
  Spinner,
} from "@nextui-org/react";
import { FiSearch } from "react-icons/fi";
import { columns, statusOptions } from "../data/quotesData";
import { useDispatch, useSelector } from "react-redux";
import { changeStatus } from "../store/slices/supplierSlice";
import axios from "axios";
import { displayDate, formatTime } from "../utils";
import { STATUS } from "../store/constants";

const statusColorMap = {
  ["awaiting approval"]: "warning",
  ["in progress"]: "warning",
  accepted: "success",
  dispute: "danger",
  rejected: "danger",
  discarded: "danger",
  cancelled: "danger",
  ["Order created"]: "secondary",
};

const INITIAL_VISIBLE_COLUMNS = [
  "status",
  "customer",
  "car",
  "company",
  "part",
  "duration",
];

const BATCH_SIZE = 100;

const Adminquotes = () => {
  const accessToken = localStorage.getItem("accessToken");
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.supplier);

  // server-backed data (batched)
  const [allQuotes, setAllQuotes] = useState([]); // concatenated backend batches
  const [batchPage, setBatchPage] = useState(0); // last backend batch number loaded
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const fetchingRef = useRef(false);
 let role = localStorage.getItem("role");
  // UI state
  const [searchTerm, setSearchTerm] = useState(""); // immediate input
  const [filterValue, setFilterValue] = useState(""); // debounced value sent to server
  const [visibleColumns] = useState(new Set(INITIAL_VISIBLE_COLUMNS));
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [page, setPage] = useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored ? Number(stored) : 1;
  });
  const [totalRecords, setTotalRecords] = useState(0);

  // sorting
  const [sortDescriptor, setSortDescriptor] = useState({
    column: null,
    direction: null,
  });

  // stable refs for callbacks to read latest values
  const filterRef = useRef(filterValue);
  const sortRef = useRef(sortDescriptor);
  const allDataLoadedRef = useRef(allDataLoaded);
  const batchPageRef = useRef(batchPage);

  useEffect(() => {
    filterRef.current = filterValue;
  }, [filterValue]);
  useEffect(() => {
    sortRef.current = sortDescriptor;
  }, [sortDescriptor]);
  useEffect(() => {
    allDataLoadedRef.current = allDataLoaded;
  }, [allDataLoaded]);
  useEffect(() => {
    batchPageRef.current = batchPage;
  }, [batchPage]);

  // debounce timer ref
  const searchTimerRef = useRef(null);

  // fetch a single backend batch (page = batchNum, pageSize = BATCH_SIZE)
  const fetchBatch = useCallback(
    async (batchNum, { replace = false, filter, sort } = {}) => {
      // read current filter/sort from refs unless provided
      const curFilter =
        typeof filter === "undefined" ? filterRef.current : filter;
      const curSort = typeof sort === "undefined" ? sortRef.current : sort;

      if (fetchingRef.current) return;
      if (allDataLoadedRef.current && !replace) return;

      try {
        fetchingRef.current = true;
        dispatch(changeStatus(STATUS.LOADING));

        await dispatch(changeStatus(STATUS.LOADING)); // keep redux state consistent (if you prefer refreshIfNeeded add it)
        // refresh token/state if you have such action:
        // await dispatch(refreshIfNeeded());

        const fresh = localStorage.getItem("accessToken");
        const auth = { headers: { Authorization: `Bearer ${fresh}` } };

        const qp = new URLSearchParams();
        qp.append("page", batchNum);
        qp.append("pageSize", BATCH_SIZE);

        if (curFilter) qp.append("search", curFilter);
        if (curSort?.column) {
          qp.append("sortBy", curSort.column);
          qp.append(
            "sortOrder",
            curSort.direction === "ascending" ? "asc" : "desc"
          );
        }

        const res = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/admin/getquotes/?${qp}`,
          auth
        );

        // backend shape: earlier code used res?.data?.details?.Quotes
        const fetchedRows =
          res?.data?.details?.Quotes || res?.data?.details?.rows || [];
        const indexObj = res?.data?.details?.index || {};
        const backendCurrentPage = indexObj.currentPage || batchNum;
        const backendTotalRecords = indexObj.totalRecords ||  totalRecords;

        setTotalRecords(backendTotalRecords);

      if (replace || batchNum === 1) {
  setAllQuotes(fetchedRows);
} else {
  setAllQuotes((prev) => {
    const prevIds = new Set(prev.map((r) => r.id));
    const uniqueNew = fetchedRows.filter((r) => !prevIds.has(r.id));
    return [...prev, ...uniqueNew];
  });
}

// 👇 Add this after setAllQuotes to force re-render
setPage((p) => p);


        setBatchPage(backendCurrentPage);
        batchPageRef.current = backendCurrentPage;

        if (fetchedRows.length < BATCH_SIZE) {
          setAllDataLoaded(true);
          allDataLoadedRef.current = true;
        } else {
          setAllDataLoaded(false);
          allDataLoadedRef.current = false;
        }

        dispatch(changeStatus(STATUS.IDLE));
      } catch (err) {
        dispatch(changeStatus(STATUS.ERROR));
        console.error(
          "fetchBatch error:",
          err?.response?.data || err.message || err
        );
      } finally {
        fetchingRef.current = false;
      }
    },
    [dispatch]
  );

  // ensure page data is loaded (fetch additional batches as needed)
const ensurePageData = useCallback(
  async (uiPage) => {
    const requiredEndIndex = uiPage * rowsPerPage;

    // If already have enough quotes for that page — skip
    if (requiredEndIndex <= allQuotes.length) return;

    // If already fetched all data from backend
    if (allDataLoadedRef.current) return;

    // Calculate which backend batch needed
    const requiredBatch = Math.ceil(requiredEndIndex / BATCH_SIZE);

    for (let b = batchPageRef.current + 1; b <= requiredBatch; b++) {
      await fetchBatch(b, { replace: false });
      if (allDataLoadedRef.current) break;
    }
  },
  [allQuotes.length, rowsPerPage, fetchBatch]
);

  // initial load and reset when filter/sort/rowsPerPage changes
  useEffect(() => {
    setAllQuotes([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    fetchBatch(1, { replace: true }).catch((e) => console.error(e));
    const id = setInterval(() => {
      if (!fetchingRef.current)
        fetchBatch(1, { replace: true }).catch(() => {});
    }, 120000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValue,
    sortDescriptor.column,
    sortDescriptor.direction,
    rowsPerPage,
  ]);

  // search debounce: update filterValue after idle
  const onSearchChange = useCallback((val) => {
    setSearchTerm(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setFilterValue(val.trim());
      setPage(1);
      localStorage.setItem("currentPage", 1);
      searchTimerRef.current = null;
    }, 500);
  }, []);

  const onClear = useCallback(() => {
    setSearchTerm("");
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setFilterValue("");
    setAllQuotes([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    localStorage.setItem("currentPage", 1);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  // header columns (unchanged)
  const headerColumns = useMemo(() => {
    if (visibleColumns === "all") return columns;
    let column = [...columns];
    if (role === "SUPPLIER")
      column = column.filter((item) => item.uid !== "company");
    else if (role === "CUSTOMER")
      column = column.filter((item) => item.uid !== "customer");
    return column.filter((colum) =>
      Array.from(visibleColumns).includes(colum.uid)
    );
  }, [visibleColumns, role]);

  // apply client-side statusFilter (server-side search is already applied)
  const filteredItems = useMemo(() => {
    let list = [...allQuotes];

    if (statusFilter !== "all") {
      list = list.filter(
        (q) => q?.QuoteStatus?.status?.toLowerCase() === statusFilter
      );
    }

    return list;
  }, [allQuotes, statusFilter]);

  // sorting helpers
  const getSortValue = (item, column) => {
    switch (column) {
      case "status":
        return (item.QuoteStatus?.status || "").toLowerCase();
      case "customer":
        return String(item.Request?.customer_id || "").toLowerCase();
      case "car":
        const make =
          item.Request?.VehicleMake?.name || item.Request?.custom_make || "";
        const model =
          item.Request?.VehicleModel?.name || item.Request?.custom_model || "";
        return `${make} ${model}`.toLowerCase().trim();
      case "company":
        return (item.User?.company_name || "").toLowerCase();
      case "part":
        return (
          item.Request?.Part?.name ||
          item.Request?.custom_part_type ||
          ""
        ).toLowerCase();
      case "duration":
        return new Date(
          item.createdAt || item.Request?.date_requested || 0
        ).getTime();
      default:
        return item[column] || "";
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortDescriptor.column) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      let v1 = getSortValue(a, sortDescriptor.column);
      let v2 = getSortValue(b, sortDescriptor.column);

      // detect date
      const isDate1 = !isNaN(Date.parse(v1));
      const isDate2 = !isNaN(Date.parse(v2));
      if (isDate1 && isDate2) {
        v1 = new Date(v1).getTime();
        v2 = new Date(v2).getTime();
      }

      const isNum1 = !isNaN(v1) && v1 !== "";
      const isNum2 = !isNaN(v2) && v2 !== "";
      if (isNum1 && isNum2) {
        return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;
      }

      v1 = v1.toString().toLowerCase();
      v2 = v2.toString().toLowerCase();
      const cmp = v1.localeCompare(v2);
      return sortDescriptor.direction === "ascending" ? cmp : -cmp;
    });
  }, [filteredItems, sortDescriptor]);

  // paginate after sorting
  const pagedItems = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  // pages derived from server totalRecords (preferred) or loaded length
  const pages = Math.max(
    1,
    Math.ceil((totalRecords) / rowsPerPage)
  );

  const renderCell = useCallback((data, columnKey) => {
    const cellValue = data[columnKey];
    switch (columnKey) {
      case "status":
        return (
          <Chip
            className="capitalize"
            color={
              statusColorMap[
                data?.QuoteStatus?.status?.toLowerCase() ?? "default"
              ]
            }
            variant="flat"
          >
            {data?.QuoteStatus?.status ?? "Not Available"}
          </Chip>
        );

      case "customer":
        return (
          <div>
            <p className="table_cell">
              {(data.Request?.User?.first_name || "") +
                " " +
                (data.Request?.User?.last_name || "") || "Not Provided"}
            </p>
          </div>
        );

      case "car":
        return (
          <div className="flex flex-col gap-1 capitalize">
            <p className="table_cell capitalize">
              {data?.Request?.VehicleMake?.name?.toLowerCase() ||
                data?.Request?.custom_make?.toLowerCase() ||
                "Not Provided"}{" "}
              {data?.Request?.VehicleModel?.name?.toLowerCase() ||
                data?.Request?.custom_model?.toLowerCase() ||
                "Not Provided"}
            </p>
          </div>
        );
      case "company":
        return (
          <div>
            <p className="table_cell capitalize">
              {data?.User?.company_name || "Not Provided"}
            </p>
          </div>
        );

      case "part":
        return (
          <div>
            <p className="table_cell capitalize">
              {data?.Request?.Part?.name?.toLowerCase() ||
                data?.Request?.custom_part_type?.toLowerCase() ||
                "Not Provided"}
            </p>
          </div>
        );

      case "duration":
        return (
          <div className="flex flex-col gap-1">
            <p className="table_cell capitalize">
              {formatTime(data?.createdAt || "Not Provided")}
            </p>
            <p className="table_cell_sm">{displayDate(data?.createdAt)}</p>
          </div>
        );

      default:
        return cellValue;
    }
  }, []);

  // handlers
  const onRowsPerPageChange = useCallback((e) => {
    const val = Number(e.target.value);
    setRowsPerPage(val);
    setPage(1);
    localStorage.setItem("currentPage", 1);
    // reset batches & fetch will be triggered by effect on rowsPerPage
  }, []);

  // search input wiring (value = searchTerm for snappy typing)
  const onSearchClear = useCallback(() => {
    onClear();
  }, [onClear]);

  const handleDownloadCSV = useCallback(() => {
    // build CSV from current visible pagedItems
    const header = [
      "Quote ID",
      "Reference",
      "Status",
      "Car",
      "Part",
      "Created At",
    ];
    const lines = pagedItems.map((r) => {
      const make = r.Request?.VehicleMake?.name || r.Request?.custom_make || "";
      const model =
        r.Request?.VehicleModel?.name || r.Request?.custom_model || "";
      const part = r.Request?.Part?.name || r.Request?.custom_part_type || "";
      const created = new Date(r.createdAt).toLocaleString();
      const cells = [
        r.id || "",
        r.reference_id || "",
        r.QuoteStatus?.status || "",
        `${make} ${model}`,
        part,
        created,
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });
    const csvContent = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "admin_quotes_report.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [pagedItems]);

const handlePageChange = useCallback(
  async (newPage) => {
    await ensurePageData(newPage);
    setPage(newPage);
    localStorage.setItem("currentPage", newPage);
  },
  [ensurePageData]
);



  // topContent & bottomContent remain same structure — wire input to searchTerm
  const topContent = useMemo(() => {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center gap-3">
          <Input
            isClearable
            className="w-full sm:max-w-[44%]"
            placeholder="Search quotes..."
            startContent={<FiSearch />}
            value={searchTerm}
            onClear={() => onSearchClear()}
            onValueChange={onSearchChange}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row flex-wrap items-center justify-between gap-y-4">
          <div>
            <Tabs
              aria-label="Options"
              variant="underlined"
              selectedKey={statusFilter}
              onSelectionChange={(key) => {
                setStatusFilter(key);
                setPage(1);
                                // setFilterValue("");
              }}
              className="mobile-only:[&>div]:grid mobile-only:[&>div]:grid-cols-2 mobile-only:[&>div]:gap-2"
            >
              <Tab key="all" title="All Quotes"></Tab>
              <Tab key="awaiting approval" title="Awaiting Approval"></Tab>
              <Tab key="in progress" title="In Progress"></Tab>
              <Tab key="accepted" title="Quotes Accepted"></Tab>
            </Tabs>
          </div>

          <div className="flex items-center gap-5 text-xs sm:text-sm text-default-400">
            <span>Total: {totalRecords} Quotes</span>
            <Divider orientation="vertical" className="h-6" />
            <label className="flex items-center">
              Rows Per Page:
              <select
                             onChange={onRowsPerPageChange} value={rowsPerPage}>
                <option value="6">6</option>
                <option value="10">10</option>
                <option value="15">15</option>
              </select>
            </label>
          </div>
        </div>
      </div>
    );
  }, [
    searchTerm,
    onSearchClear,
    onSearchChange,
    statusFilter,
    filteredItems.length,
    rowsPerPage,
    onRowsPerPageChange,
  ]);

  const bottomContent = useMemo(() => {
    return (
      <div className="py-2 px-2 flex justify-center items-center">
     <Pagination
  key={`${page}-${totalRecords}`}
  isCompact
  showControls
  showShadow
  color="primary"
  page={page}
  total={pages === 0 ? pages + 1 : pages}
  onChange={handlePageChange}
/>

      
      </div>
    );
  }, [page, totalRecords, pages]);

  return (
    <Table
      aria-label="Quotes Table"
      isHeaderSticky
      isStriped
      topContent={topContent}
      topContentPlacement="outside"
      bottomContent={bottomContent}
      bottomContentPlacement="outside"
      sortDescriptor={sortDescriptor}
      onSortChange={(newSort) => {
        setSortDescriptor(newSort);
        // update ref sync (effect above will run fetch)
        setPage(1);
        localStorage.setItem("currentPage", 1);
      }}
      classNames={{
        td: "whitespace-nowrap",
      }}
    >
      <TableHeader columns={headerColumns}>
        {(column) => (
          <TableColumn
            key={column.uid}
            align={column.uid === "actions" ? "center" : "start"}
            allowsSorting
          >
            {column.name}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody
        emptyContent={"No Quotes Found"}
        items={pagedItems || []}
        isLoading={status === STATUS.LOADING && !allQuotes.length}
        loadingContent={
          <Spinner label="Loading..." className="w-full bg-white" />
        }
      >
        {(item) => {
          // console.log("iteeemmmmmmmmm", item.id);
          return (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  <Link to={`/adminquotes/${item.id}`}>
                    {renderCell(item, columnKey)}
                  </Link>
                </TableCell>
              )}
            </TableRow>
          );
        }}
      </TableBody>
    </Table>
  );
};

export default Adminquotes;
